import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageForm from "components/pageForm";
import Button from "components/button";
import EmptyStateCompany from "components/emptyState";
import SectionWrapper from "pages/createMission/components/SectionWrapper";
import TemplateCard from "pages/missionTemplates/components/TemplateCard";
import UseContext from "hooks/useContext";
import useFetchData from "hooks/useFetchData";
import useModal from "components/modal/useModal";
import ApiDataBase from "server";
import { hasMissionTemplatesFeature } from "utils/missionTemplatesFeature";

const MissionTemplates = () => {
    useFetchData();
    const navigate = useNavigate();
    const { toggle } = useModal();
    const {
        missionTemplates,
        setMissionTemplates,
        refreshMissionTemplates,
        setPendingDeleteMissionTemplate,
        setPreviewMissionTemplate,
        resetTemplateData,
        companyData,
        missionTemplatesCompanyId,
        setMissionTemplatesCompanyId,
    } = UseContext();
    const missionTemplatesEnabled = hasMissionTemplatesFeature(companyData);
    const [isLoading, setIsLoading] = useState(() => {
        if (companyData?._id == null) return true;
        return missionTemplatesCompanyId !== String(companyData._id);
    });
    const hasCompanyLoaded = companyData?._id != null;
    const scopeIdRef = useRef(null);
    scopeIdRef.current = missionTemplatesCompanyId;
    const currentCompanyIdRef = useRef(null);
    currentCompanyIdRef.current =
        companyData?._id != null ? String(companyData._id) : null;

    useEffect(() => {
        if (hasCompanyLoaded && !missionTemplatesEnabled) {
            navigate("/createOfMission", { replace: true });
        }
    }, [hasCompanyLoaded, missionTemplatesEnabled, navigate]);

    useEffect(() => {
        if (!missionTemplatesEnabled || !companyData?._id) return undefined;
        const requestedId = String(companyData._id);
        const hasCacheForCompany = scopeIdRef.current === requestedId;
        setIsLoading(!hasCacheForCompany);

        let cancelled = false;

        ApiDataBase.getMissionTemplates(
            { idCompany: requestedId },
            (res) => {
                if (cancelled) return;
                if (requestedId !== currentCompanyIdRef.current) return;

                const list = res?.templates || [];
                setMissionTemplates(list);
                setMissionTemplatesCompanyId(requestedId);
                setIsLoading(false);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                toast.error("Impossible de charger les fiches de poste");
                setIsLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [refreshMissionTemplates, companyData?._id, missionTemplatesEnabled, setMissionTemplates, setMissionTemplatesCompanyId]);

    const handleCreate = () => {
        resetTemplateData();
        navigate("/missionTemplates/create");
    };

    const handleEdit = (template) => {
        navigate(`/missionTemplates/edit/${template._id}`);
    };

    const handleDelete = (template) => {
        setPendingDeleteMissionTemplate(template);
        toggle("missionTemplate_delete");
    };

    const handlePreview = (template) => {
        setPreviewMissionTemplate(template);
        toggle("missionTemplate_preview");
    };

    const hasTemplates = missionTemplates && missionTemplates.length > 0;

    if (!hasCompanyLoaded) {
        return (
            <PageForm>
                <div className="col-span-full">
                    <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                        <div className="flex flex-col gap-3">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="h-24 bg-gray-100 rounded-xl animate-pulse"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </PageForm>
        );
    }

    if (!missionTemplatesEnabled) {
        return null;
    }

    const showListFooter = !isLoading && hasTemplates;

    return (
        <PageForm>
            <div className="col-span-full">
                <div
                    className={`w-full mx-auto lg:w-[800px] px-6 py-8 ${
                        showListFooter ? "pb-24" : ""
                    }`}
                >
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="h-24 bg-gray-100 rounded-xl animate-pulse"
                                />
                            ))}
                        </div>
                    ) : hasTemplates ? (
                        <div className="flex flex-col gap-6">
                            <SectionWrapper
                                title="Fiches de postes"
                                description="Configurez une fiche de poste une fois et réutilisez-la à chaque mission."
                            >
                                <div className="flex flex-col gap-3">
                                    {missionTemplates.map((template) => (
                                        <TemplateCard
                                            key={template._id}
                                            template={template}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onPreview={handlePreview}
                                        />
                                    ))}
                                </div>
                            </SectionWrapper>
                        </div>
                    ) : (
                        <EmptyStateCompany
                            type={"job_post_template"}
                            onAction={handleCreate}
                        />
                    )}
                </div>
            </div>
            {showListFooter && (
                <div className="col-span-full">
                    <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                        <div className="w-full mx-auto lg:w-[800px] px-6 py-4 flex justify-end">
                            <Button
                                color="PRIMARY"
                                size="LG"
                                onClick={handleCreate}
                            >
                                Créer une fiche de poste
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default MissionTemplates;
